/**
 * Date locale configuration for internationalization
 * Provides locale-specific settings for date formatting across the application
 */

export interface DateLocaleConfig {
  /** BCP 47 language tag for Intl.DateTimeFormat */
  locale: string;
  /** Standard date format configurations */
  formats: {
    /** Short date format - used for task cards, tooltips */
    short: Intl.DateTimeFormatOptions;
    /** Compact date format - used for pills, chips (ultra-short) */
    compact: Intl.DateTimeFormatOptions;
    /** Long date format - used for detailed views */
    long: Intl.DateTimeFormatOptions;
    /** Time-only format - used for timestamps */
    time: Intl.DateTimeFormatOptions;
    /** Full datetime format - used for history, uploads */
    full: Intl.DateTimeFormatOptions;
    /** Input format - used for date inputs */
    input: Intl.DateTimeFormatOptions;
  };
  /** First day of week for calendar components (0 = Sunday, 1 = Monday) */
  firstDayOfWeek: 0 | 1;
  /** Relative time thresholds and labels */
  relativeTime: {
    justNow: number; // milliseconds
    minutes: number; // milliseconds
    hours: number; // milliseconds
    days: number; // milliseconds
  };
}

/**
 * Date locale configurations for supported languages
 */
export const DATE_LOCALES: Record<string, DateLocaleConfig> = {
  pl: {
    locale: 'pl-PL',
    formats: {
      short: {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      },
      compact: {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
      },
      long: {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      },
      time: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // 24-hour format for Polish
      },
      full: {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      },
      input: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }
    },
    firstDayOfWeek: 1, // Monday first
    relativeTime: {
      justNow: 60000, // 1 minute
      minutes: 3600000, // 1 hour
      hours: 86400000, // 1 day
      days: 604800000 // 1 week
    }
  },
  en: {
    locale: 'en-US',
    formats: {
      short: {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      },
      compact: {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      },
      long: {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      },
      time: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // 12-hour format for English
      },
      full: {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      },
      input: {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }
    },
    firstDayOfWeek: 0, // Sunday first
    relativeTime: {
      justNow: 60000, // 1 minute
      minutes: 3600000, // 1 hour
      hours: 86400000, // 1 day
      days: 604800000 // 1 week
    }
  }
};

/**
 * Get date locale configuration for a given language
 * @param language - Language code (e.g., 'pl', 'en')
 * @returns Date locale configuration
 */
export const getDateLocale = (language: string): DateLocaleConfig => {
  return DATE_LOCALES[language] || DATE_LOCALES.en as DateLocaleConfig;
};

/**
 * Date input format patterns for different locales
 */
export const DATE_INPUT_PATTERNS = {
  pl: {
    pattern: 'DD.MM.YYYY',
    placeholder: 'DD.MM.RRRR',
    regex: /^(\d{2})\.(\d{2})\.(\d{4})$/,
    separator: '.'
  },
  en: {
    pattern: 'MM/DD/YYYY',
    placeholder: 'MM/DD/YYYY',
    regex: /^(\d{2})\/(\d{2})\/(\d{4})$/,
    separator: '/'
  }
} as const;

/**
 * Get date input pattern for a given language
 * @param language - Language code
 * @returns Date input pattern configuration
 */
export const getDateInputPattern = (language: string) => {
  return DATE_INPUT_PATTERNS[language as keyof typeof DATE_INPUT_PATTERNS] || DATE_INPUT_PATTERNS.en;
};